/**
 * ============================================================================
 * Approver Applied Filters — Persistent Redux state for filter persistence
 * ============================================================================
 *
 * Stores the user's currently applied filters (FilterData) and date mode
 * so they persist across navigation and page refreshes.
 *
 * State is synced to sessionStorage for browser-level recovery and
 * survives component unmounting/mounting within the same session.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '../../store'

/** Shape of filter data from FilterComponent. */
export interface FilterData {
  startDate: string
  endDate: string
  checkboxFilters?: Record<string, string[]>
}

/**
 * Redux state for the approver's currently applied filters.
 */
export interface ApproverAppliedFiltersState {
  /** The user's current filter selections (startDate, endDate, checkboxFilters). */
  appliedFilter: FilterData | null
  /** Whether filters apply to a date range ('range') or all time ('all'). */
  dateFilterMode: 'all' | 'range'
}

const STORAGE_KEY = 'approver_applied_filters'
const STORAGE_MODE_KEY = 'approver_date_filter_mode'

/**
 * Persist applied filter to sessionStorage.
 * @param filter - The FilterData to persist, or null to clear.
 */
const persistFilter = (filter: FilterData | null): void => {
  if (filter) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filter))
  } else {
    sessionStorage.removeItem(STORAGE_KEY)
  }
}

/**
 * Persist date filter mode to sessionStorage.
 * @param mode - 'all' or 'range'.
 */
const persistDateFilterMode = (mode: 'all' | 'range'): void => {
  sessionStorage.setItem(STORAGE_MODE_KEY, mode)
}

const initialState: ApproverAppliedFiltersState = {
  appliedFilter: null,
  dateFilterMode: 'all',
}

/**
 * Redux slice for managing approver applied filters.
 */
const approverAppliedFiltersSlice = createSlice({
  name: 'approverAppliedFilters',
  initialState,
  reducers: {
    /**
     * Update the applied filter and persist to sessionStorage.
     * @param state - Current Redux state.
     * @param action - Payload containing the new FilterData.
     */
    setAppliedFilter: (state, action: PayloadAction<FilterData | null>) => {
      state.appliedFilter = action.payload
      persistFilter(action.payload)
    },

    /**
     * Update the date filter mode ('all' or 'range') and persist.
     * @param state - Current Redux state.
     * @param action - Payload containing 'all' or 'range'.
     */
    setDateFilterMode: (
      state,
      action: PayloadAction<'all' | 'range'>
    ) => {
      state.dateFilterMode = action.payload
      persistDateFilterMode(action.payload)
    },

    /**
     * Reset both filters and date mode to null/default.
     * Clears sessionStorage as well.
     * Call this when the user clicks "Reset Filters" or navigates away.
     */
    clearApproverAppliedFilters: (state) => {
      state.appliedFilter = null
      state.dateFilterMode = 'all'
      sessionStorage.removeItem(STORAGE_KEY)
      sessionStorage.removeItem(STORAGE_MODE_KEY)
    },
  },
})

/**
 * Selector: Get the currently applied filter from Redux state.
 * @param state - Root Redux state.
 * @returns The applied filter, or null if none.
 */
export const selectAppliedFilter = (state: RootState): FilterData | null =>
  state.approverAppliedFilters.appliedFilter

/**
 * Selector: Get the currently selected date filter mode.
 * @param state - Root Redux state.
 * @returns 'all' or 'range'.
 */
export const selectDateFilterMode = (
  state: RootState
): 'all' | 'range' =>
  state.approverAppliedFilters.dateFilterMode

export const {
  setAppliedFilter,
  setDateFilterMode,
  clearApproverAppliedFilters,
} = approverAppliedFiltersSlice.actions

export default approverAppliedFiltersSlice.reducer
